import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CanActivateFn } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

export const authGuardGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.isAuthenticated$.pipe(
    take(1),
    map(isAuthenticated => {
      // If user is authenticated and trying to access auth pages (login, register)
      // redirect to dashboard
      if (isAuthenticated && isAuthRoute(state.url)) {
        router.navigate(['/app/dashboard']);
        return false;
      }

      // If user is not authenticated and trying to access protected pages
      // redirect to login
      if (!isAuthenticated && !isAuthRoute(state.url)) {
        router.navigate(['/login'], { 
          queryParams: { returnUrl: state.url } 
        });
        return false;
      }

      return true;
    })
  );
};

// Helper function to check if route is an auth route
function isAuthRoute(url: string): boolean {
  const authRoutes = ['/login', '/register', '/forgot-password', '/reset-password'];
  return authRoutes.some(route => url.startsWith(route));
}

// Additional guard for admin-only routes
export const adminGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.user$.pipe(
    take(1),
    map(user => {
      if (!user || user.role !== 'admin') {
        router.navigate(['/dashboard']);
        return false;
      }
      return true;
    })
  );
};

// Guard for practitioners
export const practitionerGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.user$.pipe(
    take(1),
    map(user => {
      if (!user || (user.role !== 'practitioner' && user.role !== 'admin')) {
        router.navigate(['/dashboard']);
        return false;
      }
      return true;
    })
  );
};
